/**
 * Frontend-only mapping between a turn's knowledge-graph traces and the
 * messages they were recorded against.
 *
 * A turn's traces are spread across every assistant message the turn
 * produced, not gathered on one of them: the backbone drains its trace sink
 * once per Bedrock call and keys what it drained to the assistant message
 * that call produced (see
 * `roboto_service.ai.core.backbone.record_knowledge_graph_traces`). So the
 * passive per-turn injection, which runs before the first call, lands on the
 * turn's *first* assistant message, while each `query_knowledge` tool
 * call runs between calls and lands on the *next* assistant message. The
 * admin-facing affordance meanwhile lives on the turn's *user* message
 * (`MessageKnowledgeGraphButton`, rendered via `AdminAgentThreadViewer`'s
 * `renderMessageKnowledgeGraphControl`), so it has to reconstruct every one
 * of those assistant messages to see all of the turn's retrievals.
 * `turnAssistantSeqs` recovers their sequence numbers from the thread's own
 * message list — already loaded, no additional API call.
 */
import {
  type AgentThreadMessage,
  AgentThreadRole,
} from "@/shared/domain/agentThread/AgentThreadRecord";

/**
 * Sequence numbers of every assistant-role message the turn starting at
 * `turnUserSeq` produced, in order — one per Bedrock call of the turn, and so
 * one per message a knowledge-graph trace can be attached to.
 *
 * Reads the thread's message list positionally, whose array index equals each
 * message's sequence number ("the session record's messages list is
 * contiguous from zero" — `reconstruct_prompt`), and stops at the next user
 * message, which starts the following turn.
 */
export function turnAssistantSeqs(
  threadMessages: AgentThreadMessage[],
  turnUserSeq: number,
): number[] {
  const seqs: number[] = [];
  for (let seq = turnUserSeq + 1; seq < threadMessages.length; seq++) {
    const role = threadMessages[seq]?.role;
    if (role === AgentThreadRole.USER) {
      break;
    }
    if (role === AgentThreadRole.ASSISTANT) {
      seqs.push(seq);
    }
  }
  return seqs;
}
